import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, User, GraduationCap, Briefcase, FileText, Upload, X, MapPin, AlertCircle, Settings2 } from 'lucide-react';
import { getUser } from '@/utils/auth';
import api, { getAssetUrl } from '@/utils/api';

const ProfileManagement = () => {
  const user = getUser();
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [languageInput, setLanguageInput] = useState('');
  const [states, setStates] = useState([]);
  const [districtSuggestions, setDistrictSuggestions] = useState([]);
  const [showDistrictSuggestions, setShowDistrictSuggestions] = useState(false);
  const [districtSearchInput, setDistrictSearchInput] = useState('');
  const districtInputRef = useRef(null);
  const [currentTab, setCurrentTab] = useState('personal');
  const [skillInput, setSkillInput] = useState('');
  const [prefLocationInput, setPrefLocationInput] = useState('');
  const [prefLocationSuggestions, setPrefLocationSuggestions] = useState([]);
  const [showPrefLocationSuggestions, setShowPrefLocationSuggestions] = useState(false);
  const [profile, setProfile] = useState({
    profile_picture: '',
    full_name: '',
    father_name: '',
    mother_name: '',
    dob: '',
    gender: '',
    marital_status: '',
    languages: [],
    address: '',
    city: '',
    district: '',
    state: '',
    email: '',
    mobile: user?.mobile || '',
    physically_challenged: false,
    education: [],
    experience: [],
    cv: '',
    cv_content: '',
    skills: [],
    preferred_job_type: [],
    preferred_locations: []
  });

  const availableLanguages = ['Tamil', 'Telugu', 'Kannada', 'Malayalam', 'English', 'Hindi', 'French', 'German', 'Malay', 'Chinese', 'Japanese', 'Russian', 'Spanish', 'Arabic', 'Bengali', 'Marathi', 'Gujarati', 'Punjabi', 'Urdu'];
  const filteredLanguages = availableLanguages.filter(lang => 
    lang.toLowerCase().includes(languageInput.toLowerCase()) && !profile.languages.includes(lang)
  );

  useEffect(() => {
    fetchProfile();
    fetchStates();
  }, []);

  const fetchStates = async () => {
    try {
      const response = await api.get('/location-master/states');
      setStates(response.data);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const searchDistricts = async (query) => {
    if (query.length < 2) {
      setDistrictSuggestions([]);
      setShowDistrictSuggestions(false);
      return;
    }
    
    try {
      const response = await api.get(`/location-master/districts/search?q=${encodeURIComponent(query)}&limit=15`);
      setDistrictSuggestions(response.data);
      setShowDistrictSuggestions(response.data.length > 0);
    } catch (error) {
      console.error('Error searching districts:', error);
    }
  };

  const handleDistrictSearchChange = (e) => {
    const value = e.target.value;
    setDistrictSearchInput(value);
    searchDistricts(value);
  };

  const selectDistrictSuggestion = (suggestion) => {
    setProfile({
      ...profile,
      state: suggestion.state,
      district: suggestion.district
    });
    setDistrictSearchInput(suggestion.display);
    setDistrictSuggestions([]);
    setShowDistrictSuggestions(false);
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get(`/job-seeker/profile/${user.id}`);
      if (response.data) {
        setProfile({
          ...response.data,
          education: response.data.education || [],
          experience: response.data.experience || [],
          languages: response.data.languages || [],
          skills: response.data.skills || [],
          preferred_job_type: response.data.preferred_job_type || [],
          preferred_locations: response.data.preferred_locations || []
        });
        if (response.data.profile_picture) {
          // Use getAssetUrl which handles both R2 URLs and relative paths
          const fullImageUrl = getAssetUrl(response.data.profile_picture);
          setPhotoPreview(fullImageUrl);
        }
        // Set district search input if state/district exist
        if (response.data.state && response.data.district) {
          setDistrictSearchInput(`${response.data.state} / ${response.data.district}`);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Check file size - max 50KB for profile photo
    if (file.size > 50 * 1024) {
      toast.error('Profile photo must be less than 50KB. Please compress or resize your image.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload/profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const imagePath = response.data.path;
      setProfile({ ...profile, profile_picture: imagePath });
      
      // Use getAssetUrl which handles both R2 URLs and relative paths
      const fullImageUrl = getAssetUrl(imagePath);
      setPhotoPreview(fullImageUrl);
      toast.success('Photo uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photo');
    }
  };

  const handleCVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }

    // Check file size - max 1MB for resume
    if (file.size > 1 * 1024 * 1024) {
      toast.error('Resume must be less than 1MB. Please compress your PDF file.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload/cv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile({ ...profile, cv: response.data.path });
      toast.success('CV uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload CV');
    }
  };

  // Validation for Personal Info tab
  const validatePersonalInfo = () => {
    const errors = [];
    if (!profile.full_name?.trim()) errors.push('Full Name is required');
    if (!profile.father_name?.trim()) errors.push('Father Name is required');
    if (!profile.mother_name?.trim()) errors.push('Mother Name is required');
    if (!profile.dob) errors.push('Date of Birth is required');
    if (!profile.gender) errors.push('Gender is required');
    if (!profile.marital_status) errors.push('Marital Status is required');
    if (!profile.languages || profile.languages.length === 0) errors.push('At least one language is required');
    if (!profile.address?.trim()) errors.push('Address is required');
    if (!profile.state) errors.push('State is required');
    if (!profile.district) errors.push('District is required');
    if (!profile.city?.trim()) errors.push('City is required');
    // Email and Physically Challenged are NOT mandatory
    
    return errors;
  };

  // Validation for Education tab
  const validateEducation = () => {
    const errors = [];
    if (profile.education.length === 0) {
      errors.push('At least one education entry is required');
      return errors;
    }
    
    profile.education.forEach((edu, index) => {
      if (!edu.qualification) errors.push(`Education #${index + 1}: Qualification is required`);
      if (!edu.course?.trim()) errors.push(`Education #${index + 1}: Course is required`);
      if (!edu.institution_name?.trim()) errors.push(`Education #${index + 1}: Institution Name is required`);
      if (!edu.course_type) errors.push(`Education #${index + 1}: Course Type is required`);
      if (!edu.from_year) errors.push(`Education #${index + 1}: From Year is required`);
      if (!edu.to_year) errors.push(`Education #${index + 1}: To Year is required`);
    });
    
    return errors;
  };

  // Validation for Experience tab
  const validateExperience = () => {
    const errors = [];
    // Experience is optional - if no entries, that's fine
    
    profile.experience.forEach((exp, index) => {
      // Mandatory fields
      if (!exp.industry_type) errors.push(`Experience #${index + 1}: Type of Industry is required`);
      if (!exp.company_name?.trim()) errors.push(`Experience #${index + 1}: Company Name is required`);
      if (!exp.designation?.trim()) errors.push(`Experience #${index + 1}: Designation is required`);
      if (!exp.from_month || !exp.from_year) errors.push(`Experience #${index + 1}: From date is required`);
      if (!exp.to_month || !exp.to_year) errors.push(`Experience #${index + 1}: To date is required`);
      if (!exp.employment_type) errors.push(`Experience #${index + 1}: Employment Type is required`);
      if (!exp.salary_type) errors.push(`Experience #${index + 1}: Salary Type is required`);
      if (!exp.salary_amount) errors.push(`Experience #${index + 1}: Salary Amount is required`);
      // achievements, roles_responsibilities, promotion are NOT mandatory
    });
    
    return errors;
  };

  // Validation for CV tab
  const validateCV = () => {
    const errors = [];
    if (!profile.cv && !profile.cv_content?.trim()) {
      errors.push('Either CV Upload or CV Content is required');
    }
    return errors;
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put(`/job-seeker/profile/${user.id}`, profile);
      toast.success('Profile updated successfully!');
      fetchProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndNext = async () => {
    // Validate current tab before saving
    let errors = [];
    if (currentTab === 'personal') {
      errors = validatePersonalInfo();
    } else if (currentTab === 'education') {
      errors = validateEducation();
    } else if (currentTab === 'experience') {
      errors = validateExperience();
    }

    if (errors.length > 0) {
      errors.forEach(err => toast.error(err));
      return;
    }

    setLoading(true);
    try {
      await api.put(`/job-seeker/profile/${user.id}`, profile);
      toast.success('Saved! Moving to next section...');
      fetchProfile();
      
      // Navigate to next tab
      if (currentTab === 'personal') {
        setCurrentTab('education');
      } else if (currentTab === 'education') {
        setCurrentTab('experience');
      } else if (currentTab === 'experience') {
        setCurrentTab('preferences');
      } else if (currentTab === 'preferences') {
        setCurrentTab('cv');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalSave = async () => {
    // Validate CV tab
    const errors = validateCV();
    if (errors.length > 0) {
      errors.forEach(err => toast.error(err));
      return;
    }

    setLoading(true);
    try {
      await api.put(`/job-seeker/profile/${user.id}`, profile);
      toast.success('Profile saved successfully!');
      fetchProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (currentTab === 'education') {
      setCurrentTab('personal');
    } else if (currentTab === 'experience') {
      setCurrentTab('education');
    } else if (currentTab === 'preferences') {
      setCurrentTab('experience');
    } else if (currentTab === 'cv') {
      setCurrentTab('preferences');
    }
  };

  const handleNext = () => {
    if (currentTab === 'personal') {
      setCurrentTab('education');
    } else if (currentTab === 'education') {
      setCurrentTab('experience');
    } else if (currentTab === 'experience') {
      setCurrentTab('preferences');
    } else if (currentTab === 'preferences') {
      setCurrentTab('cv');
    }
  };

  const handleDownloadCV = () => {
    if (profile.cv) {
      // Use getAssetUrl which handles both R2 URLs and relative paths
      const cvUrl = getAssetUrl(profile.cv);
      window.open(cvUrl, '_blank');
    }
  };

  const addEducation = () => {
    setProfile({
      ...profile,
      education: [...profile.education, {
        qualification: '',
        course: '',
        institution_name: '',
        course_type: 'Full Time',
        from_year: '',
        to_year: ''
      }]
    });
  };

  const removeEducation = (index) => {
    const newEducation = profile.education.filter((_, i) => i !== index);
    setProfile({ ...profile, education: newEducation });
  };

  const updateEducation = (index, field, value) => {
    const newEducation = [...profile.education];
    newEducation[index][field] = value;
    setProfile({ ...profile, education: newEducation });
  };

  const addExperience = () => {
    setProfile({
      ...profile,
      experience: [...profile.experience, {
        industry_type: '',
        company_name: '',
        designation: '',
        from_month: '',
        from_year: '',
        to_month: '',
        to_year: '',
        years_of_experience: '',
        employment_type: '',
        salary_type: '',
        salary_amount: '',
        achievements: '',
        roles_responsibilities: '',
        promotion: false,
        promotion_details: ''
      }]
    });
  };

  const removeExperience = (index) => {
    const newExperience = profile.experience.filter((_, i) => i !== index);
    setProfile({ ...profile, experience: newExperience });
  };

  const updateExperience = (index, field, value) => {
    const newExperience = [...profile.experience];
    newExperience[index][field] = value;
    
    // Auto-calculate years of experience
    if (['from_month', 'from_year', 'to_month', 'to_year'].includes(field)) {
      const exp = newExperience[index];
      if (exp.from_month && exp.from_year && exp.to_month && exp.to_year) {
        const fromDate = new Date(exp.from_year, exp.from_month - 1);
        const toDate = new Date(exp.to_year, exp.to_month - 1);
        const months = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());
        const years = Math.floor(months / 12);
        const remainingMonths = months % 12;
        newExperience[index].years_of_experience = `${years} years ${remainingMonths} months`;
      }
    }
    
    setProfile({ ...profile, experience: newExperience });
  };

  const addLanguage = (lang) => {
    if (!profile.languages.includes(lang)) {
      setProfile({ ...profile, languages: [...profile.languages, lang] });
      setLanguageInput('');
    }
  };

  const removeLanguage = (lang) => {
    setProfile({ ...profile, languages: profile.languages.filter(l => l !== lang) });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>My Profile</h1>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-sm text-slate-600">Profile Strength</p>
            <div className="flex items-center gap-2">
              <div className="w-32 bg-slate-200 rounded-full h-2">
                <div 
                  className="bg-teal-600 h-2 rounded-full transition-all" 
                  style={{width: `${profile.profile_strength || 0}%`}}
                />
              </div>
              <span className="text-sm font-semibold text-teal-600">{profile.profile_strength || 0}%</span>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5 mb-6">
          <TabsTrigger value="personal" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">Personal</span>
          </TabsTrigger>
          <TabsTrigger value="education" className="flex items-center gap-2">
            <GraduationCap className="w-4 h-4" />
            <span className="hidden sm:inline">Education</span>
          </TabsTrigger>
          <TabsTrigger value="experience" className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            <span className="hidden sm:inline">Experience</span>
          </TabsTrigger>
          <TabsTrigger value="preferences" className="flex items-center gap-2">
            <Settings2 className="w-4 h-4" />
            <span className="hidden sm:inline">Preferences</span>
          </TabsTrigger>
          <TabsTrigger value="cv" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">CV</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="personal">
          <Card className="p-6 border-slate-200">
            <h3 className="text-xl font-semibold text-slate-900 mb-4">Personal Information</h3>
            
            {/* Info Alert */}
            <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> All fields are mandatory except Email and Physically Challenged.
              </p>
            </div>
            
            {/* Profile Photo Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Profile Photo <span className="text-red-500">*</span></label>
              <div className="flex items-center gap-4">
                {photoPreview ? (
                  <img src={photoPreview} alt="Profile" className="w-24 h-24 rounded-full object-cover border-2 border-slate-200" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center border-2 border-slate-200">
                    <User className="w-12 h-12 text-slate-400" />
                  </div>
                )}
                <div>
                  <label className="cursor-pointer">
                    <div className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-md hover:bg-slate-50 transition-colors">
                      <Upload className="w-4 h-4" />
                      <span className="text-sm">Upload Photo</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      data-testid="profile-photo-upload"
                    />
                  </label>
                  <p className="text-xs text-slate-500 mt-1">Max 50KB (JPG, PNG)</p>
                </div>
                {photoPreview && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPhotoPreview(null);
                      setProfile({ ...profile, profile_picture: '' });
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name <span className="text-red-500">*</span></label>
                <Input
                  data-testid="full-name-input"
                  value={profile.full_name}
                  onChange={(e) => setProfile({...profile, full_name: e.target.value})}
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Father Name <span className="text-red-500">*</span></label>
                <Input
                  data-testid="father-name-input"
                  value={profile.father_name}
                  onChange={(e) => setProfile({...profile, father_name: e.target.value})}
                  placeholder="Enter your father's name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Mother Name <span className="text-red-500">*</span></label>
                <Input
                  data-testid="mother-name-input"
                  value={profile.mother_name}
                  onChange={(e) => setProfile({...profile, mother_name: e.target.value})}
                  placeholder="Enter your mother's name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Date of Birth <span className="text-red-500">*</span></label>
                <Input
                  data-testid="dob-input"
                  type="date"
                  value={profile.dob}
                  onChange={(e) => setProfile({...profile, dob: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Gender <span className="text-red-500">*</span></label>
                <Select value={profile.gender} onValueChange={(value) => setProfile({...profile, gender: value})}>
                  <SelectTrigger data-testid="gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Transgender">Transgender</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Marital Status <span className="text-red-500">*</span></label>
                <Select value={profile.marital_status} onValueChange={(value) => setProfile({...profile, marital_status: value})}>
                  <SelectTrigger data-testid="marital-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Single">Single</SelectItem>
                    <SelectItem value="Married">Married</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Known Languages <span className="text-red-500">*</span></label>
                <div className="relative">
                  <Input
                    placeholder="Search and select languages..."
                    value={languageInput}
                    onChange={(e) => setLanguageInput(e.target.value)}
                    data-testid="language-search-input"
                  />
                  {languageInput && filteredLanguages.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredLanguages.map((lang) => (
                        <div
                          key={lang}
                          className="px-4 py-2 hover:bg-slate-50 cursor-pointer text-sm"
                          onClick={() => addLanguage(lang)}
                        >
                          {lang}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {profile.languages.map((lang) => (
                    <span
                      key={lang}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm"
                    >
                      {lang}
                      <button
                        type="button"
                        onClick={() => removeLanguage(lang)}
                        className="hover:text-teal-900"
                        data-testid={`remove-language-${lang}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Address <span className="text-red-500">*</span></label>
                <Textarea
                  data-testid="address-textarea"
                  rows={3}
                  value={profile.address}
                  onChange={(e) => setProfile({...profile, address: e.target.value})}
                  placeholder="Enter your full address"
                />
              </div>
              
              {/* Location Fields - Using location master DB */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">State <span className="text-red-500">*</span></label>
                <Select 
                  value={profile.state} 
                  onValueChange={(value) => {
                    setProfile({...profile, state: value, district: ''});
                    setDistrictSearchInput('');
                  }}
                >
                  <SelectTrigger data-testid="state-select">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map((state) => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">District <span className="text-red-500">*</span></label>
                <div className="relative">
                  <Input
                    ref={districtInputRef}
                    data-testid="district-input"
                    placeholder="Type to search district..."
                    value={districtSearchInput || profile.district}
                    onChange={handleDistrictSearchChange}
                    onFocus={() => {
                      if (districtSearchInput.length >= 2) {
                        setShowDistrictSuggestions(true);
                      }
                    }}
                  />
                  {showDistrictSuggestions && districtSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {districtSuggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-slate-50 text-sm border-b border-slate-100 last:border-b-0"
                          onClick={() => selectDistrictSuggestion(suggestion)}
                        >
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3 h-3 text-slate-400" />
                            <span>{suggestion.display}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">City <span className="text-red-500">*</span></label>
                <Input
                  data-testid="city-input"
                  value={profile.city}
                  onChange={(e) => setProfile({...profile, city: e.target.value})}
                  placeholder="Enter your city"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email (Optional)</label>
                <Input
                  data-testid="profile-email-input"
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({...profile, email: e.target.value})}
                  placeholder="Enter your email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Mobile</label>
                <Input
                  value={profile.mobile}
                  disabled
                  className="bg-slate-50"
                />
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={profile.physically_challenged}
                    onCheckedChange={(checked) => setProfile({...profile, physically_challenged: checked})}
                  />
                  <span className="text-sm font-medium text-slate-700">Physically Challenged (Optional)</span>
                </label>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="education">
          <Card className="p-6 border-slate-200">
            <h3 className="text-xl font-semibold text-slate-900 mb-4">Educational Details</h3>
            
            {/* Info Alert */}
            <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> All education fields are mandatory. Add at least one education entry.
              </p>
            </div>

            {profile.education.length === 0 ? (
              <div className="text-center py-8">
                <GraduationCap className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600 mb-4">No education details added yet</p>
                <Button data-testid="add-education-btn-empty" onClick={addEducation} className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Education
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {profile.education.map((edu, index) => (
                  <div key={index} className="p-4 border border-slate-200 rounded-lg">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium text-slate-900">Education #{index + 1}</h4>
                      <Button
                        data-testid={`remove-education-${index}`}
                        onClick={() => removeEducation(index)}
                        variant="ghost"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Qualification <span className="text-red-500">*</span></label>
                        <Select value={edu.qualification} onValueChange={(value) => updateEducation(index, 'qualification', value)}>
                          <SelectTrigger data-testid={`qualification-${index}`}>
                            <SelectValue placeholder="Select qualification" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10th">10th</SelectItem>
                            <SelectItem value="12th">12th</SelectItem>
                            <SelectItem value="ITI">ITI</SelectItem>
                            <SelectItem value="Diploma">Diploma</SelectItem>
                            <SelectItem value="UG Degree">UG Degree</SelectItem>
                            <SelectItem value="PG Degree">PG Degree</SelectItem>
                            <SelectItem value="PHD">PHD</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Course <span className="text-red-500">*</span></label>
                        <Input
                          data-testid={`course-${index}`}
                          value={edu.course}
                          onChange={(e) => updateEducation(index, 'course', e.target.value)}
                          placeholder="e.g., B.E Computer Science"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Institution Name <span className="text-red-500">*</span></label>
                        <Input
                          data-testid={`institution-name-${index}`}
                          value={edu.institution_name}
                          onChange={(e) => updateEducation(index, 'institution_name', e.target.value)}
                          placeholder="University/College/School"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Course Type <span className="text-red-500">*</span></label>
                        <Select value={edu.course_type} onValueChange={(value) => updateEducation(index, 'course_type', value)}>
                          <SelectTrigger data-testid={`course-type-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Full Time">Full Time</SelectItem>
                            <SelectItem value="Part Time">Part Time</SelectItem>
                            <SelectItem value="Distance">Distance</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">From Year <span className="text-red-500">*</span></label>
                        <Input
                          data-testid={`from-year-${index}`}
                          type="number"
                          value={edu.from_year}
                          onChange={(e) => updateEducation(index, 'from_year', e.target.value)}
                          placeholder="e.g., 2018"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">To Year <span className="text-red-500">*</span></label>
                        <Input
                          data-testid={`to-year-${index}`}
                          type="number"
                          value={edu.to_year}
                          onChange={(e) => updateEducation(index, 'to_year', e.target.value)}
                          placeholder="e.g., 2022"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Add Education Button - Below the form */}
                <div className="pt-4 border-t border-slate-200">
                  <Button data-testid="add-education-btn" onClick={addEducation} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Another Education
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="experience">
          <Card className="p-6 border-slate-200">
            <h3 className="text-xl font-semibold text-slate-900 mb-4">Experience Details</h3>
            
            {/* Info Alert */}
            <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> All fields are mandatory except Achievement, Roles & Responsibilities, and Promotion details.
              </p>
            </div>

            {profile.experience.length === 0 ? (
              <div className="text-center py-8">
                <Briefcase className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600 mb-4">No experience details added yet</p>
                <Button data-testid="add-experience-btn-empty" onClick={addExperience} className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Experience
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {profile.experience.map((exp, index) => (
                  <div key={index} className="p-4 border border-slate-200 rounded-lg">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium text-slate-900">Experience #{index + 1}</h4>
                      <Button
                        data-testid={`remove-experience-${index}`}
                        onClick={() => removeExperience(index)}
                        variant="ghost"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Type of Industry <span className="text-red-500">*</span></label>
                        <Select value={exp.industry_type} onValueChange={(value) => updateExperience(index, 'industry_type', value)}>
                          <SelectTrigger data-testid={`industry-type-${index}`}>
                            <SelectValue placeholder="Select industry" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Aerospace">Aerospace</SelectItem>
                            <SelectItem value="Agriculture">Agriculture</SelectItem>
                            <SelectItem value="Automotive">Automotive</SelectItem>
                            <SelectItem value="Biotechnology">Biotechnology</SelectItem>
                            <SelectItem value="Chemical">Chemical</SelectItem>
                            <SelectItem value="Construction">Construction</SelectItem>
                            <SelectItem value="Consulting">Consulting</SelectItem>
                            <SelectItem value="Education">Education</SelectItem>
                            <SelectItem value="Health Care">Health Care</SelectItem>
                            <SelectItem value="IT Software">IT Software</SelectItem>
                            <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                            <SelectItem value="Retail">Retail</SelectItem>
                            <SelectItem value="Banking">Banking</SelectItem>
                            <SelectItem value="Insurance">Insurance</SelectItem>
                            <SelectItem value="Hospitality">Hospitality</SelectItem>
                            <SelectItem value="Logistics">Logistics</SelectItem>
                            <SelectItem value="Telecom">Telecom</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Company Name <span className="text-red-500">*</span></label>
                        <Input
                          data-testid={`company-${index}`}
                          value={exp.company_name}
                          onChange={(e) => updateExperience(index, 'company_name', e.target.value)}
                          placeholder="Enter company name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Designation <span className="text-red-500">*</span></label>
                        <Input
                          data-testid={`designation-${index}`}
                          value={exp.designation}
                          onChange={(e) => updateExperience(index, 'designation', e.target.value)}
                          placeholder="Enter your designation"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">From (Month/Year) <span className="text-red-500">*</span></label>
                        <Input
                          data-testid={`exp-from-${index}`}
                          type="month"
                          value={exp.from_year && exp.from_month ? `${exp.from_year}-${exp.from_month.toString().padStart(2, '0')}` : ''}
                          onChange={(e) => {
                            const [year, month] = e.target.value.split('-');
                            updateExperience(index, 'from_year', year);
                            updateExperience(index, 'from_month', month);
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">To (Month/Year) <span className="text-red-500">*</span></label>
                        <Input
                          data-testid={`exp-to-${index}`}
                          type="month"
                          value={exp.to_year && exp.to_month ? `${exp.to_year}-${exp.to_month.toString().padStart(2, '0')}` : ''}
                          onChange={(e) => {
                            const [year, month] = e.target.value.split('-');
                            updateExperience(index, 'to_year', year);
                            updateExperience(index, 'to_month', month);
                          }}
                        />
                      </div>
                      {exp.years_of_experience && (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Experience Duration</label>
                          <Input
                            value={exp.years_of_experience}
                            disabled
                            className="bg-slate-50"
                          />
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Employment Type <span className="text-red-500">*</span></label>
                        <Select value={exp.employment_type} onValueChange={(value) => updateExperience(index, 'employment_type', value)}>
                          <SelectTrigger data-testid={`employment-type-${index}`}>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Onroll">Onroll</SelectItem>
                            <SelectItem value="Offroll">Offroll</SelectItem>
                            <SelectItem value="Contract">Contract</SelectItem>
                            <SelectItem value="Probationary">Probationary</SelectItem>
                            <SelectItem value="Permanent">Permanent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Salary Type <span className="text-red-500">*</span></label>
                        <Select value={exp.salary_type} onValueChange={(value) => updateExperience(index, 'salary_type', value)}>
                          <SelectTrigger data-testid={`salary-type-${index}`}>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Gross">Gross</SelectItem>
                            <SelectItem value="Take Home">Take Home</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Salary Amount (₹) <span className="text-red-500">*</span></label>
                        <Input
                          data-testid={`salary-${index}`}
                          type="number"
                          placeholder="Enter amount (numbers only)"
                          value={exp.salary_amount}
                          onChange={(e) => updateExperience(index, 'salary_amount', e.target.value.replace(/\D/g, ''))}
                          min="0"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Achievements in Company (Optional)</label>
                        <Textarea
                          data-testid={`achievements-${index}`}
                          rows={3}
                          placeholder="Describe your achievements..."
                          value={exp.achievements}
                          onChange={(e) => updateExperience(index, 'achievements', e.target.value)}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Roles and Responsibilities (Optional)</label>
                        <Textarea
                          data-testid={`roles-${index}`}
                          rows={3}
                          placeholder="Describe your roles and responsibilities..."
                          value={exp.roles_responsibilities}
                          onChange={(e) => updateExperience(index, 'roles_responsibilities', e.target.value)}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="flex items-center gap-2 cursor-pointer mb-2">
                          <Checkbox
                            checked={exp.promotion}
                            onCheckedChange={(checked) => updateExperience(index, 'promotion', checked)}
                            data-testid={`promotion-${index}`}
                          />
                          <span className="text-sm font-medium text-slate-700">Any Promotion in this company? (Optional)</span>
                        </label>
                        {exp.promotion && (
                          <Textarea
                            data-testid={`promotion-details-${index}`}
                            rows={3}
                            placeholder="Describe your promotion details..."
                            value={exp.promotion_details}
                            onChange={(e) => updateExperience(index, 'promotion_details', e.target.value)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Add Experience Button - Below the form */}
                <div className="pt-4 border-t border-slate-200">
                  <Button data-testid="add-experience-btn" onClick={addExperience} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Another Experience
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="preferences">
          <Card className="p-6 border-slate-200">
            <h3 className="text-xl font-semibold text-slate-900 mb-4">Job Preferences</h3>
            
            <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Adding your skills, preferred job types, and locations helps us match you with better job opportunities and send relevant weekly job alerts.
              </p>
            </div>

            {/* Skills Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Skills</label>
              <div className="flex gap-2">
                <Input
                  data-testid="skill-input"
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && skillInput.trim()) {
                      e.preventDefault();
                      const newSkill = skillInput.trim();
                      if (!profile.skills.some(s => s.toLowerCase() === newSkill.toLowerCase())) {
                        setProfile({ ...profile, skills: [...profile.skills, newSkill] });
                      }
                      setSkillInput('');
                    }
                  }}
                  placeholder="Type a skill and press Enter (e.g., Python, Excel, Marketing)"
                />
                <Button
                  data-testid="add-skill-btn"
                  variant="outline"
                  onClick={() => {
                    if (skillInput.trim()) {
                      const newSkill = skillInput.trim();
                      if (!profile.skills.some(s => s.toLowerCase() === newSkill.toLowerCase())) {
                        setProfile({ ...profile, skills: [...profile.skills, newSkill] });
                      }
                      setSkillInput('');
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {profile.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm"
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => setProfile({ ...profile, skills: profile.skills.filter((_, i) => i !== index) })}
                      className="hover:text-indigo-900"
                      data-testid={`remove-skill-${index}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">Add skills relevant to your experience (e.g., Python, Data Analysis, AutoCAD, Tally)</p>
            </div>

            {/* Preferred Job Type */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Preferred Job Type</label>
              <div className="flex flex-wrap gap-3">
                {['Full Time', 'Part Time', 'Contract', 'Internship', 'Freelance'].map((type) => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      data-testid={`pref-job-type-${type.toLowerCase().replace(' ', '-')}`}
                      checked={profile.preferred_job_type.includes(type)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setProfile({ ...profile, preferred_job_type: [...profile.preferred_job_type, type] });
                        } else {
                          setProfile({ ...profile, preferred_job_type: profile.preferred_job_type.filter(t => t !== type) });
                        }
                      }}
                    />
                    <span className="text-sm text-slate-700">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Preferred Locations */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Preferred Work Locations</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    data-testid="pref-location-input"
                    value={prefLocationInput}
                    onChange={async (e) => {
                      const value = e.target.value;
                      setPrefLocationInput(value);
                      if (value.length >= 2) {
                        try {
                          const response = await api.get(`/location-master/districts/search?q=${encodeURIComponent(value)}&limit=10`);
                          setPrefLocationSuggestions(response.data);
                          setShowPrefLocationSuggestions(response.data.length > 0);
                        } catch (err) {
                          setPrefLocationSuggestions([]);
                        }
                      } else {
                        setPrefLocationSuggestions([]);
                        setShowPrefLocationSuggestions(false);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && prefLocationInput.trim()) {
                        e.preventDefault();
                        const loc = prefLocationInput.trim();
                        if (!profile.preferred_locations.some(l => l.toLowerCase() === loc.toLowerCase())) {
                          setProfile({ ...profile, preferred_locations: [...profile.preferred_locations, loc] });
                        }
                        setPrefLocationInput('');
                        setShowPrefLocationSuggestions(false);
                      }
                    }}
                    placeholder="Search city/district and press Enter"
                  />
                  {showPrefLocationSuggestions && prefLocationSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {prefLocationSuggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-slate-50 text-sm border-b border-slate-100 last:border-b-0"
                          onClick={() => {
                            const district = suggestion.district || suggestion.display;
                            if (!profile.preferred_locations.some(l => l.toLowerCase() === district.toLowerCase())) {
                              setProfile({ ...profile, preferred_locations: [...profile.preferred_locations, district] });
                            }
                            setPrefLocationInput('');
                            setPrefLocationSuggestions([]);
                            setShowPrefLocationSuggestions(false);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3 h-3 text-slate-400" />
                            <span>{suggestion.display}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  data-testid="add-pref-location-btn"
                  variant="outline"
                  onClick={() => {
                    if (prefLocationInput.trim()) {
                      const loc = prefLocationInput.trim();
                      if (!profile.preferred_locations.some(l => l.toLowerCase() === loc.toLowerCase())) {
                        setProfile({ ...profile, preferred_locations: [...profile.preferred_locations, loc] });
                      }
                      setPrefLocationInput('');
                      setShowPrefLocationSuggestions(false);
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {profile.preferred_locations.map((loc, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm"
                  >
                    <MapPin className="w-3 h-3" />
                    {loc}
                    <button
                      type="button"
                      onClick={() => setProfile({ ...profile, preferred_locations: profile.preferred_locations.filter((_, i) => i !== index) })}
                      className="hover:text-emerald-900"
                      data-testid={`remove-pref-location-${index}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">Add cities or districts where you prefer to work</p>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="cv">
          <Card className="p-6 border-slate-200">
            <h3 className="text-xl font-semibold text-slate-900 mb-4">CV / Resume</h3>
            
            {/* Info Alert */}
            <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Either upload a CV (PDF) OR paste your CV content below. At least one is mandatory.
                </p>
              </div>
            </div>
            
            <div className="space-y-6">
              {/* Upload CV File */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Upload CV (PDF Only, Max 1MB)</label>
                <div className="flex items-center gap-4">
                  {profile.cv && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-md">
                      <FileText className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700">CV Uploaded</span>
                    </div>
                  )}
                  <label className="cursor-pointer">
                    <div className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-md hover:bg-slate-50 transition-colors">
                      <Upload className="w-4 h-4" />
                      <span className="text-sm">{profile.cv ? 'Replace CV' : 'Upload PDF'}</span>
                    </div>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleCVUpload}
                      className="hidden"
                      data-testid="cv-upload-input"
                    />
                  </label>
                  {profile.cv && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownloadCV}
                        data-testid="download-cv-btn"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Preview / Download
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setProfile({ ...profile, cv: '' })}
                      >
                        Remove
                      </Button>
                    </>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-2">Upload your CV in PDF format (Maximum 1MB)</p>
              </div>

              {/* OR Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-slate-500 font-medium">OR</span>
                </div>
              </div>

              {/* Paste CV Content - Rich Text Editor Alternative */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Paste CV Content</label>
                <div className="border border-slate-300 rounded-md overflow-hidden">
                  <div className="bg-slate-50 border-b border-slate-300 px-3 py-2">
                    <p className="text-xs text-slate-600">Format your CV content below. You can copy and paste from Word or other documents.</p>
                  </div>
                  <Textarea
                    data-testid="cv-content-textarea"
                    rows={20}
                    value={profile.cv_content}
                    onChange={(e) => setProfile({...profile, cv_content: e.target.value})}
                    placeholder={`Paste your CV content here...

Example format:
-------------------
PERSONAL SUMMARY
[Your brief summary]

WORK EXPERIENCE
Company Name | Designation | Duration
- Responsibility 1
- Responsibility 2

EDUCATION
Degree | Institution | Year

SKILLS
- Skill 1, Skill 2, Skill 3

CERTIFICATIONS
- Certification 1
- Certification 2`}
                    className="font-mono text-sm border-0 focus-visible:ring-0 rounded-none min-h-[400px]"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">You can paste your CV text content here as an alternative to uploading a file</p>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="mt-6 flex justify-between items-center">
        <div className="flex gap-3">
          {currentTab !== 'personal' && (
            <Button 
              data-testid="previous-btn"
              onClick={handlePrevious} 
              variant="outline"
              className="px-6"
            >
              Previous
            </Button>
          )}
        </div>
        
        <div className="flex gap-3">
          <Button 
            data-testid="save-profile-btn"
            onClick={handleSave} 
            variant="outline"
            className="px-8"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
          
          {currentTab !== 'cv' ? (
            <Button 
              data-testid="save-next-btn"
              onClick={handleSaveAndNext} 
              className="bg-teal-600 hover:bg-teal-700 px-8"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save & Next'}
            </Button>
          ) : (
            <Button 
              data-testid="save-final-btn"
              onClick={handleFinalSave} 
              className="bg-teal-600 hover:bg-teal-700 px-8"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Profile'}
            </Button>
          )}
          
          {currentTab !== 'cv' && (
            <Button 
              data-testid="next-btn"
              onClick={handleNext} 
              variant="outline"
              className="px-6"
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileManagement;
